import { useEffect, useMemo, useState } from "react";
import * as d3 from "d3-geo";
import { feature } from "topojson-client";
import { Tooltip } from "react-tooltip"; // <-- NEW IMPORT
import "react-tooltip/dist/react-tooltip.css";

// --- Constants ---
const WORLD_TOPOJSON_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

function computeOpacity(count: number, max: number) {
  if (!count || max <= 0) return 0.06;
  // Scale intensity from 12% to 85% opacity
  const intensity = Math.max(0.12, (count / max) * 0.85);
  return intensity;
}

// --- Component ---
export default function MapSVG({
  worldCountries = [],
  height = 420,
  theme = "light",
  dateRange = "",
  onFeatureClick,
}: {
  worldCountries?: Array<any>;
  height?: number;
  theme?: "light" | "dark";
  dateRange?: string;
  onFeatureClick?: (meta: any) => void;
}) {
  const [worldGeo, setWorldGeo] = useState<any | null>(null);
  const [hoveredPathId, setHoveredPathId] = useState<string | null>(null);
  // 1. Data Processing and Max Users
  const maxWorldUsers = useMemo(
    () =>
      worldCountries && worldCountries.length
        ? Math.max(...worldCountries.map((c: any) => c.users || 0))
        : 1,
    [worldCountries]
  );

  const countryByName = useMemo(() => {
    const m = new Map<string, any>();
    (worldCountries || []).forEach((c: any) => {
      // Map both name and code for easy lookup
      if (c.country) m.set(String(c.country).toLowerCase(), c);
      if (c.code) m.set(String(c.code).toLowerCase(), c);
    });
    // Add United States manually if data is separate
    const US_DATA = worldCountries.find((c) => c.country === "United States");
    if (US_DATA) m.set("united states", US_DATA);
    return m;
  }, [worldCountries]);

  // 2. Fetch TopoJSON (Only happens once)
  useEffect(() => {
    let mounted = true;
    const fetchWorld = async () => {
      try {
        const res = await fetch(WORLD_TOPOJSON_URL);
        const topo = await res.json();
        // Convert TopoJSON to GeoJSON features
        const geo = feature(topo, topo.objects.countries || topo.objects.land);
        if (mounted) setWorldGeo(geo);
      } catch (e) {
        console.error("Failed to load world topojson", e);
      }
    };
    fetchWorld();
    return () => {
      mounted = false;
    };
  }, []);

  // 3. Define D3 Projection and Path Generator
  // Use a standard Mercator projection, scaled to fit the SVG container.
  const projection = useMemo(() => {
    // We target a default size of 800x420 for scaling purposes
    const targetWidth = 800;
    const targetHeight = height;

    const p = d3
      .geoMercator()
      .scale(targetWidth / (2 * Math.PI)) // Default Mercator scale
      .translate([targetWidth / 2, targetHeight / 1.5]);

    // Calculate a bounding box to automatically fit the projection
    //const pathGenerator = d3.geoPath().projection(p);

    // Fit to a feature collection if available
    if (worldGeo) {
      //const bounds = pathGenerator.bounds(worldGeo);
      //const widthScale = (targetWidth / (bounds[1][0] - bounds[0][0])) * 0.95;
      //const heightScale = (targetHeight / (bounds[1][1] - bounds[0][1])) * 0.95;

      p.fitSize([targetWidth, targetHeight], worldGeo);
    }

    return p;
  }, [worldGeo, height]);

  const pathGenerator = d3.geoPath().projection(projection);

  // 4. Determine Map Colors (CSS variable access)
  const [cssVars, setCssVars] = useState({
    cardBg: "#fff",
    borderColor: "#ccc",
    primaryBlue: "#007AFF",
    textColor: "#212529",
    textSecondary: "#6c757d",
  });

  useEffect(() => {
    if (typeof document !== "undefined") {
      const style = getComputedStyle(document.documentElement);
      setCssVars({
        cardBg: style.getPropertyValue("--card-bg").trim() || "#fff",
        borderColor: style.getPropertyValue("--border-color").trim() || "#ccc",
        primaryBlue:
          style.getPropertyValue("--primary-blue").trim() || "#007AFF",
        textColor: style.getPropertyValue("--text-color").trim() || "#212529",
        textSecondary:
          style.getPropertyValue("--text-secondary").trim() || "#6c757d",
      });
    }
  }, [theme]);

  // 5. Render Logic
  const renderedPaths = useMemo(() => {
    if (!worldGeo) return null;

    return worldGeo.features.map((feature: any) => {
      const name = feature.properties?.name || feature.properties?.NAME || "";
      const featureId = `country-${name.replace(/\s/g, "-")}`; // Unique ID for hover
      const meta = countryByName.get(String(name).toLowerCase());
      const users = meta ? Number(meta.users || 0) : 0;
      const opacity = computeOpacity(users, maxWorldUsers);

      const isHovered = hoveredPathId === featureId;

      // Base Fill Color calculation
      const baseFillColor =
        users > 0
          ? `rgba(0, 122, 255, ${opacity})`
          : theme === "dark"
            ? "rgba(255,255,255,0.06)"
            : "rgba(0,0,0,0.04)";

      // Apply hover effect directly to the color variable
      const fillColor = isHovered
        ? `rgba(0, 122, 255, ${Math.min(1, opacity + 0.15)})`
        : baseFillColor;

      const strokeColor = cssVars.borderColor;
      const strokeWidth = 0.5;
      const flag = meta?.flag || "🌐";

      // 💡 CORRECT TOOLTIP CONTENT: Use raw HTML string to replicate GA look
      // Use inline style to pull CSS variables for color inside the HTML string
      const tooltipContent = `
        <div style="font-weight: 600; font-size: 13px; color: ${cssVars.textColor};">${flag} ${name}</div>
        <div style="font-size: 12px; color: ${cssVars.textSecondary};">${Number(users).toLocaleString()} users</div>
      `;

      return (
        <path
          key={featureId}
          d={pathGenerator(feature) as string}
          fill={fillColor} // Dynamic fill color
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          style={{ cursor: "pointer", transition: "fill 0.15s ease" }}
          // 💡 CORRECT REACT-TOOLTIP ATTRIBUTES
          data-tooltip-id="map-tooltip" // Links to the <Tooltip> component
          data-tooltip-html={tooltipContent} // Sets the HTML content
          // Hover handlers update the state, triggering re-render with new colors
          onMouseEnter={() => setHoveredPathId(featureId)}
          onMouseLeave={() => setHoveredPathId(null)}
          onClick={() => {
            if (onFeatureClick) {
              onFeatureClick(meta || { country: name, users: 0, flag: flag });
            }
          }}
        />
      );
    });
  }, [
    worldGeo,
    countryByName,
    maxWorldUsers,
    pathGenerator,
    onFeatureClick,
    cssVars,
    theme,
    hoveredPathId,
  ]);

  return (
    <div
      className="map-svg-container"
      style={{
        borderRadius: 12,
        background: cssVars.cardBg, // Use card background color
        position: "relative",
        overflow: "hidden",
        height: height,
      }}
    >
      {/* Date Range Overlay (Exact same style as before) */}
      {dateRange && (
        <div
          style={{
            position: "absolute",
            zIndex: 10,
            top: 15,
            left: 15,
            padding: "6px 10px",
            background: "var(--card-bg)",
            borderRadius: 6,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            fontSize: 12,
            fontWeight: 500,
            color: "var(--text-color)",
            display: "flex",
            alignItems: "center",
            border: "1px solid var(--border-color)",
          }}
        >
          {dateRange}
        </div>
      )}

      <svg width="100%" height={height} viewBox={`0 0 800 ${height}`}>
        {/* Render a clean, subtle background fill for the oceans/empty space */}
        <rect
          width="100%"
          height="100%"
          fill={theme === "dark" ? "rgba(0,0,0,0.1)" : "rgba(0,0,0,0.01)"}
        />
        {renderedPaths}
      </svg>

      <Tooltip
        id="map-tooltip"
        place="top"
        opacity={1}
        delayShow={100}
        // IMPORTANT: Ensure your CSS variables are accessible here or use hardcoded values
        style={{
          backgroundColor: cssVars.cardBg,
          color: cssVars.textColor,
          border: `1px solid ${cssVars.borderColor}`,
          borderRadius: "6px",
          padding: "6px 10px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          zIndex: 99999,
        }}
      />
    </div>
  );
}
